import React, {useEffect, useRef, useState} from "react";
import {observer} from 'mobx-react';
import ReactPlayer from 'react-player';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import FileSaver from 'file-saver';
import {
    Avatar,
    Button,
    Card,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Tooltip
} from "@mui/material";
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import GetAppIcon from '@mui/icons-material/GetApp';
import DynamicAvatar from "../Shared/DynamicAvatar";
import ConditionalComponent from "../Shared/ConditionalComponent";
import Utility from "../../Utility";
import {
    fileTypeEnum,
    inputTypeEnum, languageEnum,
    procedureNodeStyleEnum,
    procedureNodeTypeEnum,
    procedureTypeEnum,
    sessionStatusEnum
} from "../../serverClientShared/sharedEnums";
import ProcedurePreviewPlayer from "../RunProcedure/ProcedurePreviewPlayer";
import WebGL3dPlayer from "../../WebGL/WebGL3dPlayer";
import ProcedureHeader from "../RunProcedure/ProcedureHeader";
import ProcedureSidebar from "../RunProcedure/ProcedureSidebar/ProcedureSidebar";
import StorageIcon from '@mui/icons-material/Storage';
import ProcedureSummary from "../RunProcedure/ProcedureMain/ProcedureSummary";
import GenericUserIcon from '../../UniqueIcons/generic-user.svg';
import EllipsisContainer from "../Shared/EllipsisContainer";
import {sortTypeEnum} from "../../enums";
import sharedUtils, {getProcedureText} from "../../serverClientShared/sharedUtils";
import {useStores} from "../../Hooks";

const getNodeStartMilliSeconds = (sessionStart, nodeStart) => {
    return new Date(nodeStart).valueOf() - new Date(sessionStart).valueOf()
}

const getLogType = (newNode, generalStore) => {
    switch (newNode.NodeType) {
        case procedureNodeTypeEnum.Input:
            return ` (${generalStore.translate('input')})`;
        case procedureNodeTypeEnum.WARNING:
            return ` (${generalStore.translate('warning')})`
        case procedureNodeTypeEnum.QUESTION:
            return ` (${generalStore.translate('splitPoint')})`
        case procedureNodeTypeEnum.MULTI:
            return ` (${generalStore.translate('splitPoint')})`
        default:
            return ""
    }
}

const checkMissingBackData = procedure => {
    switch (procedure.ProcedureType) {
        case procedureTypeEnum.REGULAR:
            return false;
        case procedureTypeEnum.MEDIA:
            return !procedure.MediaProcedureFile;
        case procedureTypeEnum.THREE_D || procedureTypeEnum.FUSION || procedureTypeEnum.VIRTUAL_CLASS_ROOM || procedureTypeEnum.SITE_PREP:
            return !procedure.ThreeDFilesWebgl;
        default:
            return true;
    }
}

const addPauseToLogs = (nodePause, sessionStart, nodeEndTime, pauseTotal, currentLog, logs, procedure, newNode, log, generalStore) => {
    const pauseStartMillis = getNodeStartMilliSeconds(sessionStart, nodePause.PauseStart) - pauseTotal;
    const pauseDurationMillis = getNodeStartMilliSeconds(nodePause.PauseStart, nodePause.PauseEnd || nodeEndTime) || 0;
    currentLog.logEnd = pauseStartMillis;
    logs.push({...currentLog});
    procedure.NestedNodes.push({...newNode})
    logs.push({
        logName: generalStore.translate('sessionPaused'),
        stepHeader: "",
        logTime: pauseStartMillis,
        logEnd: pauseStartMillis + 1,
        logType: "",
        nodeId: log.nodeId,
        paused: true,
    })
    const pausedNode = {...newNode};
    pausedNode.isPaused = true;
    procedure.NestedNodes.push(pausedNode);
    currentLog.logTime = pauseStartMillis + 1;
    currentLog.logEnd = log.logEnd - pauseTotal;
    currentLog.logName = `${generalStore.translate('resumed')} `;
    currentLog.stepHeader = log.stepHeader;
    currentLog.isSubEvent = false;
    currentLog.isError = log.isError;
    return pauseTotal + pauseDurationMillis
}

const initialContextMenuState = {
    mouseX: null,
    mouseY: null,
    rightClickedItem: null
};

export const SessionPlayerModal = observer(() => {
    const {modalStore, procedureStore, generalStore, projectStore} = useStores();

    const [logSelected, setLogSelected] = useState(true);
    const [trackWidth, setTrackWidth] = useState(0);
    const [procedure, setProcedure] = useState(null);
    const [dataBoxOn, setDataBoxOn] = useState(false);
    const [updateDataInterval, setUpdateDataInterval] = useState(null);
    const [playerSettings, setPlayerSettings] = useState({
        backgroundColorRight: null,
        backgroundColorLeft: null,
        backgroundImageAddress: null,
        isBackgroundColor: null,
    })

    const [previewDialogFile, setPreviewDialogFile] = useState(null);
    const [contextMenuState, setContextMenuState] = useState(initialContextMenuState);
    const [excelJS, setExcelJS] = useState(null);

    const {session, updateSession} = modalStore.modalData;

    const playerTrack = useRef();
    const fullScreenRef = useRef();

    useEffect(() => {
        if (!!session && !!session.sessionRaw) {
            if (!session.sessionRaw.Duration) {  //if dealing with old data that does not have correct Duration recorded
                session.sessionRaw.Duration = new Date(session.sessionRaw.EndTimeStamp).valueOf() - new Date(session.sessionRaw.StartTimeStamp).valueOf()
            }
            const {logs, procedure} = createSessionLogs(session);
            procedureStore.sessionLogSteps = logs;

            setProcedure(procedure)
            if (!!procedure && ([procedureTypeEnum.FUSION, procedureTypeEnum.THREE_D, procedureTypeEnum.VIRTUAL_CLASS_ROOM, procedureTypeEnum.SITE_PREP].includes(procedure.ProcedureType))) {
                procedureStore.procedure = procedure;
            }
            procedureStore.currentSession = session.sessionRaw
        }
        return () => {
            procedureStore.currentSession.Paused = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procedureStore, session, generalStore])

    useEffect(() => {
        import('exceljs').then(module => {
            setExcelJS(module)
        });

        if (!playerTrack.current) {
            return
        }
        const handleResize = () => {
            if (playerTrack.current) {
                setTrackWidth(playerTrack.current.offsetWidth);
            }
        }
        handleResize();
        window.addEventListener("resize", handleResize)

        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, []);

    useEffect(() => {
        if (!!playerTrack.current) {
            setTrackWidth(playerTrack.current.offsetWidth)
        }
        // eslint-disable-next-line
    }, [playerTrack.current])

    useEffect(() => {
        window.addEventListener("popstate", () => closeModal)
        return () => {
            window.removeEventListener("popstate", () => closeModal)
        }
    })

    const createSessionLogs = (session) => {
        const sessionRaw = session.sessionRaw;
        let procedure = {...sessionRaw};
        procedure.IsPublished = true;
        procedure.NestedNodes = [];

        const sessionStart = sessionRaw.StartTimeStamp;
        const totalNodes = sessionRaw.DebrifNodeEntries.length;
        const duration = session.duration;
        const logs = [];

        const pauses = {};
        if (!!sessionRaw.PauseInstances && sessionRaw.PauseInstances.length > 0) {
            sessionRaw.PauseInstances.forEach(pause => {
                pause.SubEventTime = pause.PauseStart;
                if (!pauses[String(pause.NodeEntryIndex)]) {
                    pauses[String(pause.NodeEntryIndex)] = [pause]
                } else pauses[String(pause.NodeEntryIndex)].push(pause)
            })
        }

        let pauseTotal = 0

        for (let i = 0; i < totalNodes; i++) {
            const sessionNode = sessionRaw.DebrifNodeEntries[i];
            const nodePauses = pauses[String(i)];
            let newNode;
            if (!!sessionNode.NodeRef) {
                newNode = sessionNode.NodeRef;
            } else newNode = {
                NodeType: procedureNodeTypeEnum.START,
                Identifier: "fake-node",
                StepHeader: "",
                StepText: "",
                ConnectionsData: [],
                NodeMediaFilesRefs: [],
            }

            // if (i > 0) {
            //     const previousNode = procedure.NestedNodes[i - 1]
            //     if (previousNode.NodeType === procedureNodeTypeEnum.MULTI || previousNode.NodeType === procedureNodeTypeEnum.QUESTION) {
            //         previousNode.userSelection = newNode.Identifier
            //     }
            // }
            newNode.userSelection = sessionNode.SelectedNode;
            newNode.selectedNodes = sessionNode.SelectedNodesAtCurrentStep;     //to accurately represent current state of sidebar

            const logTime = getNodeStartMilliSeconds(sessionStart, sessionNode.NodeStarTime) - pauseTotal;
            let logEnd;
            if (i < totalNodes - 1) {
                logEnd = getNodeStartMilliSeconds(sessionStart, sessionRaw.DebrifNodeEntries[i + 1].NodeStarTime) - pauseTotal;
            } else {
                logEnd = duration
            }
            let stepHeader = Utility.removeHtmlTags(getProcedureText(newNode, "StepHeader", procedure));

            let logName;
            if (sessionNode.ProcedureInProcedureName) {
                logName = "Started <span class='step-header'>" + sessionNode.ProcedureInProcedureName + '</span>, step: ';
            } else if (i === 0) {
                logName = `${generalStore.translate('startedProcedure')} `
            } else if (newNode.NodeType === procedureNodeTypeEnum.START) {
                logName = generalStore.translate('navigatedBackToStart')
            } else if (sessionNode.NodeRef.StepHeader === 'Quality Survey') {
                const appFeedback = sessionRaw.AppFeedbackScore;
                const instructionFeedback = sessionRaw.InstructionsFeedbackScore;

                logName = `Survey answered: <br/> `
                stepHeader  = `Is the application easy to use? ${appFeedback}/5 <br/> Is the instruction easy to follow? ${instructionFeedback}/5`
            } else {
                logName = `${generalStore.translate('navigatedTo')} `;
            }

            let inputParams;
            if (i === totalNodes - 1) {
                inputParams = newNode.InputParams
            } else {
                inputParams = sessionNode.InputParams;
            }

            const log = {
                logName,
                stepHeader,
                logTime,
                logEnd,
                logType: getLogType(newNode, generalStore),
                nodeId: newNode.Identifier
            }

            newNode.sessionInput = "";

            if (!!inputParams && !!inputParams.Value) {
                log.logInput = inputParams.Type !== inputTypeEnum.IMAGE ? inputParams.Value : generalStore.translate('imageUploaded')
                //TODO change later to give link to media/image that was uploaded
                newNode.sessionInput = log.logInput
            }

            if (sessionNode.WarningAcknowledged) {
                log.warningAcknowledged = true
            }


            const subEvents = [...(sessionNode.NodeSubEvents || []), ...(nodePauses || [])];
            const subEventSort = Utility.buildSortMethod(true, "SubEventTime", sortTypeEnum.DATE);
            subEvents.sort(subEventSort);

            if (subEvents.length > 0) {

                const currentLog = {...log};
                delete currentLog.logInput;
                // eslint-disable-next-line no-loop-func
                subEvents.forEach(subEvent => {
                    if (!!subEvent.PauseStart && !subEvent.PauseEnd) {       //if pause is final pause, do not add to sub-events
                        return
                    }
                    if (!!subEvent.PauseStart) {    //check if sub-event is a pause event
                        pauseTotal = addPauseToLogs(subEvent, sessionStart, sessionNode.NodeEndTime, pauseTotal, currentLog, logs, procedure, newNode, log, generalStore);
                    } else {
                        const subEventTime = getNodeStartMilliSeconds(sessionStart, subEvent.SubEventTime) - pauseTotal;
                        currentLog.logEnd = subEventTime;
                        if (currentLog.logName === `${generalStore.translate('resumed')} `) {
                            currentLog.stepHeader = log.stepHeader;
                            currentLog.isSubEvent = false;
                            currentLog.isError = log.isError;
                        }
                        logs.push({...currentLog});
                        const subEventNode = {...newNode};
                        subEventNode.userSelection = subEvent.SelectedNode;
                        procedure.NestedNodes.push(subEventNode);
                        currentLog.logName = `${generalStore.translate('userAction')} `;
                        currentLog.stepHeader = subEvent.SubEventText;
                        currentLog.logTime = subEventTime;
                        currentLog.logEnd = log.logEnd - (pauseTotal || 0);
                        currentLog.isSubEvent = true;
                        currentLog.isError = subEvent.IsError;
                        currentLog.mediaIndex = subEvent.MediaIndex;
                    }
                });
                if (!!log.logInput) {
                    currentLog.logInput = log.logInput
                }
                logs.push(currentLog)
            } else logs.push(log)

            procedure.NestedNodes.push(newNode)
        }

        const preferredLanguage = generalStore.userDetails?.PreferredLanguage
        if (!!preferredLanguage && procedureStore.getAvailableNodeLanguages(procedure).map(langKey => languageEnum[langKey]).includes(preferredLanguage)) {
            procedure.selectedLanguage = preferredLanguage;
        } else procedure.selectedLanguage = session.sessionRaw.ViewedLanguage || procedure.DefaultLanguage || languageEnum.ENGLISH_US;

        if (procedure.ProcedureType !== procedureTypeEnum.REGULAR || !!sessionRaw.PayPerClickId || (sessionRaw.AbandonedNode && sessionRaw.AbandonedNode === sessionStatusEnum.COMPLETED)) {
            logs.push({
                logName: generalStore.translate('endedProcedure'),
                stepHeader: "",
                isInput: false,
                isWarning: false,
                logTime: duration,
                logEnd: duration + 1,    //to create time period for selection/highlighting of end point
                logType: ""
            })

            procedure.NestedNodes.push({    //TODO possible change to set this to pre-designed end node from editor
                NodeType: procedureNodeTypeEnum.END,
                Identifier: "procedure-end",
                StepHeader: generalStore.translate('end'),
            })
        } else {
            logs.push({
                logName: generalStore.translate(/*sessionRaw.RunIn3D ? 'sessionInProgress' : */'sessionPaused'),
                stepHeader: "",
                isInput: false,
                isWarning: false,
                logTime: duration,
                logEnd: duration + 1,    //to create time period for selection/highlighting of end point
                logType: "",
                paused: true,
                finalPause: true,
            })
            const endPausedNode = {...procedure.NestedNodes[procedure.NestedNodes.length - 1]};
            endPausedNode.isPaused = true
            procedure.NestedNodes.push(endPausedNode)
        }

        if (checkMissingBackData(procedure)) {
            procedure = null
        }

        return {logs, procedure}
    };

    const onExit = () => {
        updateDataInterval && clearInterval(updateDataInterval);
    }
    const onEnter = async () => {
        procedureStore.sessionViewerOpen = true;
        procedureStore.currentSession.Paused = false;
        const {
            webBackgroundColorRight: backgroundColorRight,
            webBackgroundColorLeft: backgroundColorLeft,
            webBackgroundImageAddress: backgroundImageAddress,
            webIsBackgroundColor: isBackgroundColor,
        } = await generalStore.getPlayerSettings();

        setPlayerSettings({
            ...playerSettings,
            backgroundColorRight,
            backgroundColorLeft,
            backgroundImageAddress,
            isBackgroundColor,
        });

        if (updateSession) {
            setUpdateDataInterval(setInterval(() => {
                if (generalStore.userIdle) {
                    return;
                }
                updateSession()
            }, 10 * 1000));
        }

    }

    const closeModal = () => {
        let payPerClick = modalStore.modalData.payPerClick;     //to account for sessions opened from payPerClick analytics modal
        modalStore.resetModalData();
        if (!!payPerClick) {
            modalStore.modalData = {
                payPerClick,
            };
        }
        modalStore.isSessionPlayerModalOpen = false;
        procedureStore.resetSession();
    };

    const downloadSessionLogs = async () => {
        const logs = procedureStore.sessionLogSteps;
        if (!logs || logs.length === 0 || !excelJS) {
            return;
        }

        const workbook = new excelJS.Workbook();
        const worksheet = workbook.addWorksheet('Session Logs');

        // Add header row
        worksheet.addRow([
            generalStore.translate('logEntry'),
            generalStore.translate('time'),
            generalStore.translate('inputIfAny'),
            generalStore.translate('warningAcknowledgedIfAny')
        ]);

        // Add data rows
        logs.forEach(logStep => {
            const row = [];
            let stepHeader = '', logName = '';
            if (logStep.logName) {
                logName = logStep.logName.split(/<span.*?>/).join('').split('</span>').join('');
            }
            if (logStep.stepHeader) {
                stepHeader = logStep.stepHeader
                    .split('&nbsp;')
                    .join('');
            }
            row.push(logName + stepHeader + logStep.logType);
            row.push(sharedUtils.renderDisplayTime(logStep.logTime));
            if (!!logStep.logInput) {
                row.push(logStep.logInput)
            } else row.push("")
            if (!!logStep.warningAcknowledged) {
                row.push(generalStore.translate('yes'))
            } else row.push("")
            worksheet.addRow(row);
        });

        const {userName, sessionRaw, procedureName} = session;

        // Generate buffer and download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${userName}_${procedureName}_${Utility.renderFileNameDate(new Date(sessionRaw.StartTimeStamp))}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    const renderHeader = () => {
        const user = session.sessionRaw.UserId;
        const viewedLanguage = session.sessionRaw.ViewedLanguage;
        const userName = user ? `${user.Name} ${user.LastName}` : generalStore.translate('guest');
        const userIcon = user ? user.UserIcon : GenericUserIcon;
        return (
            <div className="player-header">
                <div className="user-section">
                    <DynamicAvatar
                        className="user-avatar"
                        user={user}
                        imageSrc={userIcon}
                    />

                    <EllipsisContainer value={userName}>
                        <div className="user-name">
                            {userName}
                        </div>
                    </EllipsisContainer>
                </div>

                <EllipsisContainer value={session.procedureName}>
                    <div className="procedure-name">
                        {session.procedureName}
                    </div>
                </EllipsisContainer>
                <div className="session-date">
                    {session.date}
                </div>
                <div className="session-length">
                    {sharedUtils.renderDisplayTime(session.duration)}
                </div>
                <ConditionalComponent if={!!viewedLanguage}>
                    <div className="session-language">
                        {generalStore.translate('viewedIn')}: {Utility.getLanguageName(viewedLanguage)}
                    </div>
                </ConditionalComponent>
                <div className="device-type">
                    {session.Device}
                </div>
            </div>
        )
    };

    const renderDataBox = () => {
        return (
            <>
                <div className={"session-data-box" + (!dataBoxOn ? ' hidden' : '')}>
                    <div className="data-toggle">
                        <div
                            className={`data${logSelected ? " selected" : ""}`}
                            onClick={() => setLogSelected(true)}
                        >
                            {generalStore.translate('logs')}
                        </div>
                        <div
                            className={`data${logSelected ? "" : " selected"}`}
                            onClick={() => setLogSelected(false)}
                        >
                            {generalStore.translate('media')}
                        </div>
                    </div>
                    <ConditionalComponent if={logSelected}>
                        <div className="data-logs-container">
                            <div className="logs-list">
                                {procedureStore.sessionLogSteps.map((log, i) =>
                                    <React.Fragment key={i}>
                                        <div
                                            className={`session-log${i === procedureStore.currentSessionIndex ? " current-log" : ""}${log.isSubEvent ? " sub-event" : ""}${log.isError ? " is-error" : ""}`}
                                            onClick={() => procedureStore.goToLog(i, log, procedure)}
                                        >
                                            <div className="log-name">
                                                <span dangerouslySetInnerHTML={{__html: log.logName}}/>
                                                <span className="step-header"
                                                      dangerouslySetInnerHTML={{__html: log.stepHeader}}/>
                                                <span>{!!log.logType && !log.isSubEvent ? log.logType : ""}</span>
                                                <span>{!!log.finalPause ? ` (${generalStore.translate('currentLocation')})` : ""}</span>
                                            </div>
                                            <div
                                                className="log-time">{log.logName === generalStore.translate('sessionPaused') && !log.finalPause
                                                ? ""
                                                : sharedUtils.renderDisplayTime(log.logTime)
                                            }</div>
                                        </div>
                                        <ConditionalComponent if={!!log.logInput}>
                                            <div className="session-log">
                                                <div className="log-input">
                                                    {generalStore.translate('finalInputValue')} <span
                                                    className="input-value">{log.logInput}</span>
                                                </div>
                                                <div className="log-time"/>
                                            </div>
                                        </ConditionalComponent>
                                        <ConditionalComponent if={!!log.warningAcknowledged}>
                                            <div className="session-log">
                                                <div className="log-input">
                                                    <span className="input-value">
                                                        {generalStore.translate('userAcknowledgedWarning')}
                                                    </span>
                                                </div>
                                                <div className="log-time"/>
                                            </div>
                                        </ConditionalComponent>
                                    </React.Fragment>
                                )}
                            </div>
                            <Button
                                variant="outlined"
                                color="primary"
                                className="log-export-btn"
                                endIcon={<SystemUpdateAltOutlinedIcon/>}
                                onClick={downloadSessionLogs}
                            >
                                {generalStore.translate('exportLogs')}
                            </Button>
                        </div>
                    </ConditionalComponent>
                    <ConditionalComponent if={!logSelected}>
                        {renderMedia()}
                    </ConditionalComponent>
                </div>
                <IconButton onClick={() => setDataBoxOn(!dataBoxOn)} className='set-data-box-button show-in-mobile'>
                    <StorageIcon/>
                </IconButton>
            </>
        )
    }

    const renderMedia = () => {
        const sessionMedia = getMedia()

        return (
            <div className="session-media">
                <div className="data-media-container special-scroll">
                    {[...sessionMedia.values()].map(media => {
                        const file = media.File;

                        const openMedia = () => setPreviewDialogFile(media)

                        return (
                            <Card
                                key={file.FileId}
                                onClick={openMedia}
                                className="file-card"
                                onContextMenu={(e) => openContextMenu(e, file)}
                            >
                                <div className="thumbnail-container">
                                    <div className='thumbnail'>
                                        <ConditionalComponent if={!!media.Comment}>
                                            <Avatar className="comment-icon">
                                                <ChatIcon fontSize="small"/>
                                            </Avatar>
                                        </ConditionalComponent>
                                        {Utility.renderFileThumbnail(file)}
                                        <ConditionalComponent if={file.FileType === fileTypeEnum.VIDEO}>
                                            <Avatar className="play-icon">
                                                <PlayArrowIcon/>
                                            </Avatar>
                                        </ConditionalComponent>
                                    </div>
                                </div>
                                <EllipsisContainer value={file.FileName}>
                                    <div className='file-card-file-name'>
                                        {file.FileName}
                                    </div>
                                </EllipsisContainer>
                            </Card>
                        )
                    })}
                </div>
                <Button
                    variant="outlined"
                    color="primary"
                    className="download-media-btn"
                    endIcon={<GetAppIcon/>}
                    onClick={() => downloadAllMedia(sessionMedia)}
                >
                    {generalStore.translate('downloadAllMedia')}
                </Button>
            </div>
        )
    }

    const getMedia = () => {
        if (!session || !session.sessionRaw) {
            return new Set()
        }
        const media = new Set(session.sessionRaw.SessionMedia || []);
        if (session.sessionRaw.SessionRecordedMediaFile) {
            media.add({File: session.sessionRaw.SessionRecordedMediaFile})
        }
        //TODO add individual node session recordings (if this is the way we chose to record the session)
        // session.sessionRaw.DebrifNodeEntries.forEach(nodeEntry => {
        //     if(nodeEntry.RecordedMediaFile) {
        //         media.push(nodeEntry.RecordedMediaFile)
        //     }
        // })
        return media
    }

    const renderMediaDialog = () => {
        const file = previewDialogFile ? previewDialogFile.File : {};
        const comment = previewDialogFile ? previewDialogFile.Comment : "";

        return (
            <Dialog
                open={!!previewDialogFile}
                onClose={() => setPreviewDialogFile(null)}
                maxWidth='md'
            >
                <div className='file-preview-dialog p-3'>
                    <CloseIcon className="close-icon" onClick={() => setPreviewDialogFile(null)}/>
                    <div className="file-preview-title"></div>
                    {Utility.renderFilePreview(file, generalStore.translate)}
                    <div className="file-preview-comment"></div>
                </div>
            </Dialog>
        )
    }

    const downloadAllMedia = sessionMedia => {
        if (sessionMedia.size === 0) {
            return
        }
        const user = session.sessionRaw.UserId;
        const timeStrArr = new Date(session.sessionRaw.StartTimeStamp).toString().split(" ");
        const zipName = user.Name
            + user.LastName
            + session.sessionRaw.ProcedureName
            + "[" + timeStrArr.slice(1, 4).join("") + "]"
            + "[" + timeStrArr[4].split(":").join("") + "]"

        const zip = new JSZip();

        sessionMedia.forEach(media => {
            const fileName = `${media.File.FileName}.${media.File.FileExtension}`
            return zip.file(fileName, urlToPromise(media.File.FileId), {binary: true})
        });
        zip.generateAsync({type: "blob"}).then(blob => {
            FileSaver.saveAs(blob, `${zipName}.zip`);
        });
    }

    const urlToPromise = url => {
        return new Promise(function (resolve, reject) {
            JSZipUtils.getBinaryContent(url, function (err, data) {
                if (err) {
                    reject(err);
                } else {
                    resolve(data);
                }
            });
        });
    }

    const openContextMenu = (event, item) => {
        event.preventDefault();
        setContextMenuState(() => ({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            rightClickedItem: item
        }));
    };

    const closeContextMenu = (event) => {
        event && event.preventDefault();
        setContextMenuState(() => (initialContextMenuState));
    };

    const renderContextMenu = () => {
        const {mouseY, mouseX} = contextMenuState;
        return (
            <Menu
                keepMounted
                open={mouseY !== null}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    mouseY !== null && mouseX !== null
                        ? {top: mouseY, left: mouseX}
                        : undefined
                }
            >
                <MenuItem onClick={downloadMedia}>{generalStore.translate('download')}</MenuItem>
                <MenuItem onClick={addMediaToProject}>{generalStore.translate('addToProjectMedia')}</MenuItem>
                <MenuItem onClick={deleteMedia}>{generalStore.translate('delete')}</MenuItem>
            </Menu>
        )
    }

    const downloadMedia = () => {
        const file = contextMenuState.rightClickedItem;
        const fileName = `${file.FileName}.${file.FileExtension}`;
        FileSaver.saveAs(file.FileId, fileName);
        closeContextMenu();
    }

    const addMediaToProject = async () => {
        const file = contextMenuState.rightClickedItem;
        const fileName = `${file.FileName}.${file.FileExtension}`;
        const blob = await fetch(file.FileId).then(r => r.blob());
        const newFile = await new File([blob], fileName, {type: `${file.FileType}/*`})
        await generalStore.uploadFile1([newFile], session.sessionRaw.ProjectRef);
        closeContextMenu();
    }

    const deleteMedia = () => {
        const file = contextMenuState.rightClickedItem;
        generalStore.deleteSessionMedia(file.FileId, session.sessionRaw.ProcedureSessionId).then();
        let sessionRecordedMedia = session.sessionRaw.SessionRecordedMediaFile;
        if (!!sessionRecordedMedia && sessionRecordedMedia.FileId === file.FileId) {
            sessionRecordedMedia = null;
        } else {
            let sessionMedia = session.sessionRaw.SessionMedia;
            const delIndex = sessionMedia.findIndex(m => m.File.FileId === file.FileId);
            sessionMedia.splice(delIndex, 1);
        }
        projectStore.refreshSessionsPage().then()
        closeContextMenu();
    }

    const renderReplayBox = () => {
        const procedureType = procedure && procedure.ProcedureType ? procedure.ProcedureType : procedureTypeEnum.REGULAR;
        const currentSessionNode = session.sessionRaw.DebrifNodeEntries[procedureStore.currentSessionIndex] || {};
        const nodeHasRecording = !!currentSessionNode && !!currentSessionNode.RecordedMediaFile && currentSessionNode.RecordedMediaFile.FileId;

        if (nodeHasRecording) {
            document.exitFullscreen().then();
        }

        let player;
        switch (procedureType) {
            case procedureTypeEnum.THREE_D:
                player = <WebGL3dPlayer procedure={procedure}/>;
                break;
            case procedureTypeEnum.SITE_PREP:
            case procedureTypeEnum.VIRTUAL_CLASS_ROOM:
            case procedureTypeEnum.FUSION:
                player =
                    <>
                        {/*<ConditionalComponent if={!procedureStore.play3d}>*/}
                        {/*    <div className='file-preview-container'*/}
                        {/*         style={{*/}
                        {/*             background: playerSettings.isBackgroundColor ?*/}
                        {/*                 `linear-gradient(259.25deg, ${playerSettings.backgroundColorRight} 12.76%, ${playerSettings.backgroundColorLeft} 86.63%)` :*/}
                        {/*                 `url(${playerSettings.backgroundImageAddress})`*/}
                        {/*         }}*/}
                        {/*    >*/}
                        {/*<ConditionalComponent if={!!procedure.ThreeDFilesWebgl}>*/}
                        {/*    <div className='play-fusion-button'>*/}
                        {/*        <IconButton onClick={() => procedureStore.play3d = true}>*/}
                        {/*            <PlayCircleOutlineIcon/>*/}
                        {/*        </IconButton>*/}
                        {/*    </div>*/}
                        {/*</ConditionalComponent>*/}
                        {/*    </div>*/}
                        {/*</ConditionalComponent>*/}
                        <ConditionalComponent if={procedureStore.play3d}>
                            <WebGL3dPlayer procedure={procedure}/>
                        </ConditionalComponent>
                    </>
                break;
            default:
                player = null;
        }

        return (
            <div className="session-replay-box">
                {nodeHasRecording
                    ? <div className="video-wrapper">
                        <ReactPlayer
                            className="session-replay-video-player"
                            url={currentSessionNode.RecordedMediaFile.FileId}
                            playing={!!procedureStore.sessionPlayerRunning}
                            controls={false}
                            volume={10}
                            muted={false}
                            height="none"
                            width="none"
                        />
                    </div>
                    : <React.Fragment>
                        {procedureType === procedureTypeEnum.REGULAR || procedureType === procedureTypeEnum.MEDIA
                            ? <div className="simulated-replay-container">
                                {!!procedure && (!!procedure.NestedNodes[procedureStore.currentSessionIndex] || !!procedure.MediaProcedureFile)
                                    ? <ProcedurePreviewPlayer
                                        procedure={procedure}
                                        selectedNodeId={null}
                                        backgroundColorRight={playerSettings.backgroundColorRight}
                                        backgroundColorLeft={playerSettings.backgroundColorLeft}
                                        backgroundImageAddress={playerSettings.backgroundImageAddress}
                                        isBackgroundColor={!!playerSettings.isBackgroundColor}
                                        isSessionReview
                                    />
                                    : <span>{generalStore.translate('noRecordedSessionMedia')}</span>
                                }
                            </div>
                            :
                            <div className='procedure-player-summary-container' ref={fullScreenRef}>
                                <ProcedureSummary isPreview={true} position={procedureNodeStyleEnum.TOP}/>
                                <ProcedureSummary isPreview={true} position={procedureNodeStyleEnum.FLOAT}/>
                                <ProcedureSummary isPreview={true} position={procedureNodeStyleEnum.BOTTOM}/>
                                <ProcedureSummary isPreview={true} position={procedureNodeStyleEnum.RIGHT}/>
                                <ProcedureSummary isPreview={true} position={procedureNodeStyleEnum.LEFT}/>
                                <div
                                    className={`run-procedure-container${generalStore.isInFullscreen ? "" : " standard-view"}`}
                                >
                                    <div
                                        className={`procedure-player-container${generalStore.isInFullscreen ? " in-fullscreen" : ''}`}>
                                        <ProcedureHeader
                                            isPreview={true}
                                            withFullscreen={true}
                                            fullscreenRefElement={fullScreenRef.current}
                                        />
                                        <div className="procedure-body">
                                            <ConditionalComponent if={procedureStore.sidebar}>
                                                <ProcedureSidebar isPreview={false}/>
                                            </ConditionalComponent>
                                            <div
                                                className={`procedure-body-container ${procedure.ProcedureType === procedureTypeEnum.FUSION ? 'digital-twin' : ''} without-footer`}>
                                                {player}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </React.Fragment>
                }
            </div>
        )
    };

    const renderTimeline = () => {
        const logs = procedureStore.sessionLogSteps;
        if (logs.length === 0) {     //to prevent crashing before all data loaded to component
            return
        }
        const duration = logs[logs.length - 1].logTime;
        const activeStepTooltip = (logs[procedureStore.currentSessionIndex].logName + logs[procedureStore.currentSessionIndex].stepHeader)
            .split('&nbsp;')
            .join('');

        return (
            <div className="player-timeline">
                <ConditionalComponent if={!!procedureStore.sessionPlayerRunning}>
                    <IconButton
                        className="start-stop-session-player"
                        onClick={() => procedureStore.pauseSession()}
                        aria-label="toggle play/pause session"
                    >
                        <PauseIcon/>
                    </IconButton>
                </ConditionalComponent>
                <ConditionalComponent if={!procedureStore.sessionPlayerRunning}>
                    <IconButton
                        className="start-stop-session-player"
                        onClick={() => procedureStore.playSession(procedure)}
                        aria-label="toggle play/pause session"
                    >
                        <PlayArrowIcon/>
                    </IconButton>
                </ConditionalComponent>
                <IconButton
                    className="skip-session-player"
                    onClick={() => procedureStore.skipLog(1, procedure)}
                    aria-label="skip-previous"
                >
                    <SkipPreviousIcon/>
                </IconButton>
                <IconButton
                    className="skip-session-player"
                    onClick={() => procedureStore.skipLog(-1, procedure)}
                    aria-label="skip-next"
                >
                    <SkipNextIcon/>
                </IconButton>
                <div className="session-player-display-time">
                    {sharedUtils.renderDisplayTime(procedureStore.currentSessionMilliSecond)} / {sharedUtils.renderDisplayTime(session.duration)}
                </div>
                <div className="session-player-track" ref={playerTrack} id="sessionPlayerTrack">
                    {procedureStore.sessionLogSteps.map((log, i) => {

                        const tooltipText = (log.logName + log.stepHeader)
                            .split('&nbsp;')
                            .join(' ')
                            .split(/<span.*?>/).join('')
                            .split('</span>').join('');

                        const stepOffset = ((log.logTime / duration) * trackWidth - 3) || 0;
                        return (
                            <Tooltip title={tooltipText} key={`log-tick-${i}`}>
                                <div
                                    onClick={() => procedureStore.goToLog(i, log, procedure)}
                                    className="session-player-log-tick"
                                    style={{left: stepOffset}}
                                />
                            </Tooltip>
                        )
                    })}
                    <Tooltip
                        title={activeStepTooltip}>
                        <div
                            className="session-player-current-tick"
                            style={{left: ((procedureStore.currentSessionMilliSecond / duration) * trackWidth - 7) || 0}}
                        />
                    </Tooltip>
                    <div
                        className="session-player-completed-time"
                        style={{width: duration > 0 ? (procedureStore.currentSessionMilliSecond / duration) * trackWidth : 0}}
                    />
                </div>
            </div>
        );
    };


    return (
        <Dialog
            TransitionProps={{
                onEnter,
                onExit
            }}
            onClose={closeModal}
            open={modalStore.isSessionPlayerModalOpen}
            maxWidth='xl'
            fullWidth
            className='huge-modal session-player-container'
            PaperProps={{className: 'session-player-paper'}}
        >
            <Button onClick={closeModal} className='close-button close-session-player'>
                <CloseIcon htmlColor='#F2F2F2'/>
            </Button>
            {!!session
                ? <>
                    <DialogTitle className="dialog-header">
                        {generalStore.translate('sessionReview')}
                    </DialogTitle>
                    <DialogContent className='dialog-content'>
                        <div className="session-player">
                            {renderHeader()}
                            <div className="main-player">
                                {renderDataBox()}
                                {renderReplayBox()}
                            </div>
                            {renderTimeline()}
                        </div>
                    </DialogContent>
                </>
                : null
            }
            {renderMediaDialog()}
            {renderContextMenu()}
            {/*{modalStore.isSessionPlayerModalOpen ? <WebGLSessionPlayer session={session}/> : <></>}*/}
        </Dialog>
    )
});
